import { Outlet, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { StagingBanner, isStaging } from '../StagingBanner';
import { PWAInstallButton } from '../PWAInstallButton';
import { useAuthStore } from '@/lib/store/authStore';
import { UserRole } from '@/lib/types';

export default function DashboardLayout() {
  const location = useLocation();
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const isClient = user?.role === UserRole.CLIENT;

  // Admin navigation
  const adminNavigation = [
    { nameKey: 'dashboard', href: '/', icon: '📊' },
    { nameKey: 'products', href: '/products', icon: '📦' },
    { nameKey: 'categories', href: '/categories', icon: '🏷️' },
    { nameKey: 'prompts', href: '/prompts', icon: '🤖' },
    { nameKey: 'coupons', href: '/coupons', icon: '🎟️' },
    { nameKey: 'giftboxes', href: '/giftboxes', icon: '🎁' },
    { nameKey: 'orders', href: '/orders', icon: '🛍️' },
    { nameKey: 'users', href: '/users', icon: '👥' },
    { nameKey: 'apiKeys', href: '/api-keys', icon: '🔑' },
    { nameKey: 'notifications', href: '/notifications', icon: '📤' },
    { nameKey: 'settings', href: '/settings', icon: '⚙️' },
  ];

  // Client navigation
  const clientNavigation = [
    { nameKey: 'myGiftboxes', href: '/giftboxes', icon: '🎁' },
    { nameKey: 'settings', href: '/settings', icon: '⚙️' },
  ];

  const navigation = isClient ? clientNavigation : adminNavigation;

  return (
    <div className="min-h-screen bg-gray-50">
      <StagingBanner />
      
      {/* Mobile Header */}
      <header className={`lg:hidden fixed left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm ${isStaging() ? 'top-[42px]' : 'top-0'}`}>
        <div className="flex items-center justify-between px-3 py-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors touch-manipulation flex-shrink-0"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {sidebarOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <img 
                src="/logo.svg" 
                alt="YouPass Logo" 
                className="w-8 h-8 flex-shrink-0"
              />
              <div className="min-w-0">
                <div className="font-bold text-gray-900 text-sm truncate">YouPass</div>
                <div className="text-xs text-gray-500 truncate">{isClient ? t('navigation.client') : t('navigation.admin')}</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <LanguageSwitcher />
            {isStaging() && (
              <div className="px-2 py-1 bg-gradient-to-r from-orange-500 to-yellow-500 text-white text-xs font-bold uppercase rounded-md">
                ⚠️ STAGING
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Desktop Header */}
      <header className={`hidden lg:block fixed left-64 right-0 z-40 bg-white border-b border-gray-200 shadow-sm ${isStaging() ? 'top-[42px]' : 'top-0'}`}>
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex-1"></div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            {isStaging() && (
              <div className="px-3 py-1.5 bg-gradient-to-r from-orange-500 to-yellow-500 text-white text-xs font-bold uppercase tracking-wider rounded-md">
                ⚠️ STAGING
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
              className={`lg:hidden fixed inset-0 bg-black/50 z-40 ${isStaging() ? 'top-[42px]' : 'top-0'}`}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className={`lg:hidden fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200 z-50 overflow-y-auto ${isStaging() ? 'top-[42px]' : 'top-0'}`}
            >
              <div className="flex flex-col h-full">
                {/* Logo */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <img 
                      src="/logo.svg" 
                      alt="YouPass Logo" 
                      className="w-10 h-10 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-gray-900 truncate">YouPass</div>
                      <div className="text-xs text-gray-500 truncate">{isClient ? t('navigation.clientArea') : t('navigation.adminPanel')}</div>
                    </div>
                  </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-1">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href || 
                      (item.href !== '/' && location.pathname.startsWith(item.href));
                    
                    return (
                      <Link
                        key={item.nameKey}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`
                          flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative touch-manipulation
                          ${isActive
                            ? 'bg-pink-50 text-pink-600 font-medium'
                            : 'text-gray-700 hover:bg-gray-100'
                          }
                        `}
                      >
                        {isActive && (
                          <motion.div
                            layoutId="activeTab"
                            className="absolute inset-0 bg-pink-50 rounded-lg"
                            transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                          />
                        )}
                        <span className="text-xl relative z-10">{item.icon}</span>
                        <span className="relative z-10">{t(`navigation.${item.nameKey}`)}</span>
                      </Link>
                    );
                  })}
                </nav>

                {/* Logout + PWA */}
                <div className="p-4 border-t border-gray-200 space-y-2">
                  <button
                    type="button"
                    onClick={() => { setSidebarOpen(false); void logout(); }}
                    className="flex w-full items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors touch-manipulation"
                  >
                    <span className="text-xl">🚪</span>
                    <span>{t('common.logout')}</span>
                  </button>
                  <PWAInstallButton />
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <aside className={`hidden lg:block fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200 ${isStaging() ? 'top-[42px]' : 'top-0'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <img 
                src="/logo.svg" 
                alt="YouPass Logo" 
                className="w-10 h-10 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-gray-900 truncate">YouPass</div>
                <div className="text-xs text-gray-500 truncate">{isClient ? t('navigation.clientArea') : t('navigation.adminPanel')}</div>
              </div>
            </div>
            {/* Staging Badge */}
            {isStaging() && (
              <div className="mt-3 px-3 py-1.5 bg-gradient-to-r from-orange-500 to-yellow-500 text-white text-xs font-bold uppercase tracking-wider rounded-md text-center shadow-md">
                ⚠️ STAGING
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || 
                (item.href !== '/' && location.pathname.startsWith(item.href));
              
              return (
                <Link
                  key={item.nameKey}
                  to={item.href}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative
                    ${isActive
                      ? 'bg-pink-50 text-pink-600 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-pink-50 rounded-lg"
                      transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <span className="text-xl relative z-10 flex-shrink-0">{item.icon}</span>
                  <span className="relative z-10">{t(`navigation.${item.nameKey}`)}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout + PWA */}
          <div className="p-4 border-t border-gray-200 space-y-2">
            <button
              type="button"
              onClick={() => void logout()}
              className="flex w-full items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <span className="text-xl flex-shrink-0">🚪</span>
              <span>{t('common.logout')}</span>
            </button>
            <PWAInstallButton />
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`lg:ml-64 ${isStaging() ? 'pt-[42px] lg:pt-[84px]' : 'pt-[64px] lg:pt-[73px]'}`}>
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
